/**
 * `cycleBreadcrumbLabel` — derive the breadcrumb segment for a cycle.
 *
 * Issue 011 fix. Before this helper landed, every cycle-page route
 * registered `Cycle <8-char-uuid-prefix>` as the second breadcrumb
 * segment. Founder verbatim: "it should be the name of the logged
 * cycle". The UUID prefix tells a multi-cycle lead nothing about
 * which cycle they're on; the window it covers does.
 *
 * #1717 — nobody names a schedule any more. The stored `name` column
 * is ignored here (legacy pilot rows still carry whatever someone
 * typed in 2026; showing it would make the breadcrumb disagree with
 * every other surface, all of which now render the derived
 * date-range label from the backend's `_cycle_label.py`). The
 * breadcrumb wants the *compact* form of that identity, so it derives
 * `Month YYYY` from the horizon rather than echoing the full range —
 * one segment in a chain has no room for "28 Jul – 3 Aug 2026".
 *
 * Fallback chain (deliberate ordering):
 *
 *   1. Horizon-month label — `Intl.DateTimeFormat` of `horizon_start`
 *      formatted as `Month YYYY` (e.g. "May 2026"). Always rendered in
 *      `en-GB` locale so the breadcrumb reads the same on every
 *      machine — the breadcrumb is engineering-doc surface, not
 *      user-localised UI.
 *   2. `Schedule <id-prefix>` — the legacy form, kept as the final
 *      fallback so the breadcrumb still renders something while the
 *      cycle query is in flight (`cycle === undefined`) or if the
 *      horizon is missing/malformed.
 *
 * Pure function — no React, no fetch — so the helper is reused by
 * `CyclePage.tsx` (which already has the cycle in scope) and the
 * `CycleRoutes.tsx` route wrappers (which fetch via
 * `useCycle(cycleId)` and pass the response in).
 */

/**
 * Subset of the cycle response shape we depend on. The two consumers
 * (`CyclePage.tsx` + `useCycle`) both narrow to this minimal shape so
 * the helper doesn't pull the full `schema.gen.ts` `Cycle` type.
 */
export interface CycleBreadcrumbInput {
  id: string;
  /** #1717 — accepted but ignored. Kept on the interface so the two
   * call sites can keep passing whole API rows (and so legacy rows with
   * a stored name still type-check) without the helper honouring it. */
  name?: string | null;
  horizon_start?: string | null;
}

/** ISO date `YYYY-MM-DD` → `Month YYYY` ("2026-05-01" → "May 2026"). */
function horizonMonthLabel(horizonStart: string): string | null {
  // The backend stores `horizon_start` as a date-only string. Parse it
  // with explicit `T00:00:00Z` so timezone slop doesn't shift the
  // displayed month at month boundaries.
  const iso = /^\d{4}-\d{2}-\d{2}$/.test(horizonStart) ? `${horizonStart}T00:00:00Z` : horizonStart;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString("en-GB", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}

export function cycleBreadcrumbLabel(cycle: CycleBreadcrumbInput | undefined): string {
  // #1717 — `cycle.name` is deliberately NOT consulted; the window is
  // the identity.
  if (cycle?.horizon_start) {
    const horizonLabel = horizonMonthLabel(cycle.horizon_start);
    if (horizonLabel) return horizonLabel;
  }
  // Final fallback — used while the cycle query is loading and when
  // a malformed row reaches the SPA. Matches the legacy shape so any
  // muscle memory the founder built around the old breadcrumb still
  // recognises the segment.
  const id = cycle?.id ?? "";
  return id ? `Roster ${id.slice(0, 8)}` : "Roster";
}
